import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronRight, BookOpen, FileText, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function QueueRow({ item, checked, onToggle, onEdited, projectId }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.response);
  const [saving, setSaving] = useState(false);
  const key = `${item.doc_id}-${item.idx}`;
  const ad = item.auto_drafted || {};
  const save = async (accept) => {
    if (!text.trim()) { toast.error('The answer cannot be empty'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/rfp/response/projects/${projectId}/auto-draft-queue/edit`, { doc_id: item.doc_id, idx: item.idx, text, accept }, { withCredentials: true });
      toast.success(accept ? 'Saved and accepted (modified)' : 'Draft saved — still pending review');
      setEditing(false); onEdited();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };
  return (
    <div className={`border rounded-md p-2 space-y-1 ${checked ? 'border-[#4ABFCF] bg-[#4ABFCF]/5' : 'border-slate-200 bg-white'}`} data-testid={`rfp-adq-row-${key}`}>
      <div className="flex items-start gap-2">
        <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 accent-[#0F2040]" data-testid={`rfp-adq-check-${key}`} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 truncate">{item.doc_title}{item.section ? ` · ${item.section}` : ''}{item.category ? ` · ${item.category}` : ''} · row {item.idx + 1}</p>
          <p className="text-xs font-medium text-[#0F2040]" data-testid={`rfp-adq-question-${key}`}>{item.text}</p>
          {editing ? (
            <div className="mt-1 space-y-1" data-testid={`rfp-adq-editor-${key}`}>
              <textarea value={text} onChange={e => setText(e.target.value)} rows={4} className="w-full text-xs border border-slate-200 rounded-md p-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]" data-testid={`rfp-adq-textarea-${key}`} />
              <div className="flex items-center gap-1.5">
                <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving} onClick={() => save(true)} data-testid={`rfp-adq-save-accept-${key}`}>
                  {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}Save & accept
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={saving} onClick={() => save(false)} data-testid={`rfp-adq-save-draft-${key}`}>Save draft</Button>
                <button type="button" className="text-[10px] text-slate-500 hover:underline" onClick={() => { setEditing(false); setText(item.response); }} data-testid={`rfp-adq-cancel-edit-${key}`}>Cancel</button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap" data-testid={`rfp-adq-draft-${key}`}>{item.response}</p>
          )}
          <div className="mt-1 flex items-center gap-3">
            <button type="button" onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1 text-[10px] text-indigo-700 hover:underline" data-testid={`rfp-adq-sources-toggle-${key}`}>
              {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}Sources
              {ad.narrative && <Badge variant="outline" className="text-[9px] py-0 bg-sky-50 text-sky-700 border-sky-200"><FileText className="w-2.5 h-2.5 mr-0.5" />narrative</Badge>}
              {ad.block_title && <Badge variant="outline" className="text-[9px] py-0 bg-violet-50 text-violet-700 border-violet-200"><BookOpen className="w-2.5 h-2.5 mr-0.5" />library</Badge>}
            </button>
            {!editing && <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-[10px] text-slate-600 hover:text-[#0F2040] hover:underline" data-testid={`rfp-adq-edit-${key}`}><Pencil className="w-3 h-3" />Edit</button>}
            {ad.edited_at && <span className="text-[10px] text-slate-400" data-testid={`rfp-adq-edited-${key}`}>edited by {ad.edited_by}</span>}
          </div>
          {open && (
            <div className="text-[10px] text-slate-600 bg-slate-50 border border-slate-100 rounded p-2 mt-1 space-y-0.5" data-testid={`rfp-adq-sources-${key}`}>
              {ad.narrative ? <p><b>Narrative:</b> {ad.narrative}</p> : null}
              {ad.block_title ? <p><b>Library block:</b> {ad.block_title}</p> : null}
              {ad.original ? <p className="whitespace-pre-wrap"><b>Original draft:</b> {ad.original}</p> : null}
              <p className="text-slate-400">Auto-drafted {ad.at ? new Date(ad.at).toLocaleString() : ''}{ad.by ? ` by ${ad.by}` : ''}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Annexures tab — every auto-drafted row still pending review, accept / reject in bulk. */
export function AutoDraftReviewQueue({ project, onRefresh }) {
  const [queue, setQueue] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/projects/${project.id}/auto-draft-queue`, { withCredentials: true });
      setQueue(r.data); setSelected(new Set());
    } catch { setQueue({ pending: [], counts: {} }); }
  }, [project.id]);
  useEffect(() => { load(); }, [load, project.documents]);
  if (!queue) return null;
  const { pending, counts } = queue;
  if (!pending.length && !counts.accepted && !counts.rejected) return null;
  const keyOf = i => `${i.doc_id}-${i.idx}`;
  const toggle = i => setSelected(s => { const n = new Set(s); const k = keyOf(i); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const decide = async (decision, all = false) => {
    const items = all ? [] : pending.filter(i => selected.has(keyOf(i))).map(i => ({ doc_id: i.doc_id, idx: i.idx }));
    if (!all && !items.length) { toast.error('Select at least one drafted row'); return; }
    if (all && !window.confirm(`Accept all ${pending.length} auto-drafted rows?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/auto-draft-queue/decide`, { items, all, decision }, { withCredentials: true });
      toast.success(decision === 'accept' ? `${r.data.updated} row${r.data.updated === 1 ? '' : 's'} accepted` : `${r.data.updated} row${r.data.updated === 1 ? '' : 's'} sent back for manual rewrite`);
      await load(); onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update rows'); }
    finally { setBusy(false); }
  };
  const allSelected = pending.length > 0 && selected.size === pending.length;
  return (
    <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3 space-y-2" data-testid="rfp-autodraft-queue">
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setCollapsed(c => !c)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2040]" data-testid="rfp-adq-toggle">
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          <Sparkles className="w-4 h-4 text-indigo-600" />Auto-draft review queue
        </button>
        <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid="rfp-adq-pending-count">{counts.pending} pending</Badge>
        {counts.accepted > 0 && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="rfp-adq-accepted-count">{counts.accepted} accepted</Badge>}
        {counts.rejected > 0 && <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200" data-testid="rfp-adq-rejected-count">{counts.rejected} rejected</Badge>}
        <span className="text-[10px] text-slate-500">Rejected rows keep the draft text and become "Manual required" so the rep can rewrite from it</span>
        {pending.length > 0 && !collapsed && (
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <label className="inline-flex items-center gap-1 text-[10px] text-slate-600 mr-1">
              <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(pending.map(keyOf)))} className="accent-[#0F2040]" data-testid="rfp-adq-select-all" />select all
            </label>
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || !selected.size} onClick={() => decide('accept')} data-testid="rfp-adq-accept-selected-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}Accept selected{selected.size ? ` (${selected.size})` : ''}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700 border-rose-200 hover:bg-rose-50" disabled={busy || !selected.size} onClick={() => decide('reject')} data-testid="rfp-adq-reject-selected-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" />Reject selected
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => decide('accept', true)} data-testid="rfp-adq-accept-all-btn">Accept all {pending.length}</Button>
          </div>
        )}
      </div>
      {!collapsed && (pending.length ? (
        <div className="space-y-1.5 max-h-[520px] overflow-y-auto pr-1" data-testid="rfp-adq-list">
          {pending.map(i => <QueueRow key={keyOf(i)} item={i} checked={selected.has(keyOf(i))} onToggle={() => toggle(i)} projectId={project.id} onEdited={async () => { await load(); onRefresh(); }} />)}
        </div>
      ) : <p className="text-xs text-slate-500" data-testid="rfp-adq-empty">Every auto-drafted row has been reviewed.</p>)}
    </div>
  );
}
